import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';

const CONFIRMATION_INPUT_ID = 'anonymise-client-confirmation';

interface AnonymiseClientDialogProps {
  clientName: string;
  isSubmitting: boolean;
  onConfirm: (confirmation: string) => void;
  onCancel: () => void;
}

export function AnonymiseClientDialog({
  clientName,
  isSubmitting,
  onConfirm,
  onCancel,
}: AnonymiseClientDialogProps) {
  const { t } = useTranslation('clients');
  const [confirmation, setConfirmation] = useState('');
  const inputReference = useRef<HTMLInputElement>(null);
  const matchesName =
    confirmation.trim().toLocaleLowerCase() === clientName.trim().toLocaleLowerCase();

  return (
    <ModalOverlay onRequestClose={onCancel} initialFocusRef={inputReference}>
      <div className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-zoowhite border border-zooborder rounded-3xl shadow-2xl p-7">
        <div className="w-14 h-14 rounded-2xl bg-zooerrorbg flex items-center justify-center mb-4">
          <Icon name="ShieldAlert" className="text-xl text-zooerrortext" />
        </div>
        <h2 className="text-lg font-bold text-zooindigo">{t('privacy.anonymiseTitle')}</h2>
        <p className="text-sm text-zoomuted mt-1.5">{t('privacy.anonymiseRemoved')}</p>
        <p className="text-sm text-zoomuted mt-1.5">{t('privacy.anonymiseKept')}</p>
        <p className="text-sm font-semibold text-zooerrortext mt-1.5">
          {t('privacy.anonymiseIrreversible')}
        </p>
        <div className="space-y-1.5 mt-4">
          <Label htmlFor={CONFIRMATION_INPUT_ID}>
            {t('privacy.anonymiseConfirmLabel', { name: clientName })}
          </Label>
          <Input
            id={CONFIRMATION_INPUT_ID}
            ref={inputReference}
            value={confirmation}
            autoComplete="off"
            onChange={function updateConfirmation(changeEvent) {
              setConfirmation(changeEvent.target.value);
            }}
          />
        </div>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" className="flex-1" disabled={isSubmitting} onClick={onCancel}>
            {t('privacy.anonymiseCancel')}
          </Button>
          <Button
            variant="destructive"
            className="flex-1"
            disabled={matchesName === false}
            isLoading={isSubmitting}
            onClick={function confirmAnonymisation() {
              onConfirm(confirmation);
            }}
          >
            {t('privacy.anonymiseAction')}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
